import { Box, Button, Group, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import useFetch from '../helpers/useFetch';
import Spinner from './Spinner';

// The three ways the probe can fail have opposite remedies - two are "wait", one is
// "your CNAME points at somebody else's server" - so they are never collapsed into a
// single "not working" state. `healthy` picks the same green/red treatment the DNS
// rows use in DnsRecordsTable.
const PROBE_STATES = {
  healthy: {
    healthy: true,
    title: 'The click-tracking host is serving',
    advice: 'DNS, TLS and our link tracking are all working. Nothing to do here.',
  },
  skipped: {
    healthy: false,
    title: 'Not checked - the CNAME is not published yet',
    advice: 'Publish the CNAME record shown above, then re-check.',
  },
  unreachable: {
    healthy: false,
    title: 'Nothing answered on the click-tracking host',
    advice:
      'The record resolves but nothing responded - usually the TLS certificate is still being issued, or the change has not finished propagating. This normally clears on its own within the hour, and there is nothing for you to fix. Contact support@releva.ai if it is still failing after that.',
  },
  serverError: {
    healthy: false,
    title: 'The click-tracking host answered that it is unhealthy',
    advice:
      'This is usually on our side and temporary. Wait a few minutes and re-check; if it keeps failing, contact support@releva.ai.',
  },
  wrongPayload: {
    healthy: false,
    title: 'A different server is answering for this hostname',
    advice:
      'The CNAME resolves to something that is not our click-tracking service - a parked page, your own web host or a CDN. Repoint it at the value shown above.',
  },
};

// Live end-to-end check of the click-tracking host, which the API requires for
// campaigns of every channel. The request makes a real outbound call, so it runs only
// when the merchant is looking at it (settings section render, modal open) and when
// they press re-check - never on a timer and never from a widely mounted component.
const ClickTrackingHostHealth = ({ accountId, domainId }) => {
  const fetch = useFetch();
  const [health, setHealth] = useState();
  const [loading, setLoading] = useState(true);
  // Identifies the most recently issued probe, so a response from a stale request -
  // superseded by a domain switch or a second re-check press - cannot overwrite the
  // verdict for what is now on screen.
  const latestRequestId = useRef(0);

  const check = useCallback(() => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/clickTrackingHealth`)
      .then((result) => {
        if (latestRequestId.current === requestId) setHealth(result);
      })
      .catch(() => {
        if (latestRequestId.current === requestId) setHealth(null);
      })
      .finally(() => {
        if (latestRequestId.current === requestId) setLoading(false);
      });
  }, [accountId, domainId, fetch]);

  useEffect(() => {
    // accountId/domainId changed (or this is the initial mount) - the in-flight probe,
    // if any, belongs to the previous domain, so drop its verdict rather than showing it
    // while the new one loads. The explicit re-check button skips this and keeps the
    // current verdict visible.
    setHealth(undefined);
    check();
  }, [check]);

  if (health === undefined && loading) {
    return <Spinner />;
  }

  const recheckButton = (
    <Button variant="light" size="xs" loading={loading} onClick={check} leftSection={<IconRefresh size={14} />}>
      Re-check
    </Button>
  );

  // No readable probe state: the request failed, or the API is older than this build.
  // Say so plainly rather than guessing at green or red.
  const probeState = PROBE_STATES[health?.probe];
  if (!probeState) {
    return (
      <Group justify="space-between" wrap="nowrap" mt="xs">
        <Text size="xs" c="dimmed">
          Could not check whether the click-tracking host is serving.
        </Text>
        {recheckButton}
      </Group>
    );
  }

  const checkedRelative = DateTime.fromISO(health.checkedAt).toRelative({ locale: 'en' });

  return (
    <Box
      mt="xs"
      p="sm"
      style={{
        backgroundColor: probeState.healthy ? 'var(--mantine-color-success-3)' : 'var(--mantine-color-danger-3)',
      }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box>
          <Text size="sm" fw={500}>
            {probeState.title}
          </Text>
          <Text size="xs">{probeState.advice}</Text>
          <Text size="xs" mt={4} style={{ wordBreak: 'break-all' }}>
            {health.clickTrackingDomain}
            {checkedRelative && ` - checked ${checkedRelative}`}
          </Text>
        </Box>
        {recheckButton}
      </Group>
    </Box>
  );
};

export default ClickTrackingHostHealth;
